import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import CircularProgress from '@material-ui/core/CircularProgress';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import TableContainer from '@material-ui/core/TableContainer';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';

import { inject, observer } from 'mobx-react';
import QuantitySelector from '@components/QuantitySelector';
import { withStyles } from '@material-ui/core/styles';
import _get from 'lodash/get';
import _cloneDeep from 'lodash/cloneDeep';
import _findIndex from 'lodash/findIndex';
import dayjs from 'dayjs';
import BundleCriteria from '@components/BundleCriteria';

var isToday = require('dayjs/plugin/isToday');
dayjs.extend(isToday);
import { useTranslation } from 'next-i18next';

const styles = theme => ({
  thHead: {
    color: '#808EAA',
    fontSize: 14,
  },
  td: {
    color: '#31415E',
  },
  removeIcon: {
    cursor: 'pointer',
  },
  variant: {
    marginBottom: 0,
  },
  itemContent: {
    width: '100%',
  },
  itemContentWrapper: {
    display: 'flex',
    flexFlow: 'column',
  },
  itemContentRowWrapper: {
    display: 'flex',
  },
  itemImageWrapper: {
    marginRight: 13,
    minWidth: 67,
    [theme.breakpoints.up('sm')]: {
      marginRight: 16,
    },
  },
  mobileBottomContainer: {
    marginTop: 16,
  },
  topContent: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  itemImage: {
    borderRadius: 6,
  },
  addedDate: {
    color: '#FF7C1E',
  },
  addedDateWrapper: {
    paddingTop: 8,
    display: 'flex',
    alignItems: 'center',
  },
  notAllowHoldShipping: {
    color: '#808EAA',
  },
  notAllowHoldShippingWrapper: {
    paddingTop: 30,
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.up('sm')]: {
      paddingTop: 15,
    },
  },
  addedDateIcon: {
    paddingRight: 5,
    position: 'relative',
    top: 2,
  },
  remarks: {
    color: '#808EAA',
  },
  outOfStock: {
    color: '#ff4d4f',
  },
  primaryColor: {
    color: '#076BD1',
  },
  itemRemarks: {
    color: '#808EAA',
  },
  mobileItemColumn: {
    minWidth: 100,
    textAlign: 'right',
  },
  mobileItemLabel: {
    color: '#808EAA',
    width: 60,
    textAlign: 'left',
  },
  bundleDealEditActionContainer: {
    backgroundColor: '#FEFAEB',
    borderRadius: 10,
    padding: '10px 12px',
    display: 'flex',
    justifyContent: 'space-between',
    color: '#076BD1',
    marginTop: 10,
    cursor: 'pointer',
  },
  bundleCriteriaWrapper: {
    backgroundColor: '#FEFAEB',
    borderRadius: 10,
    padding: '10px 12px',
  },
  container: {
    marginTop: 10,
    maxHeight: 'calc(100vh - 170px)',
    [theme.breakpoints.up('sm')]: {
      maxHeight: 440,
    },
  },
  alertWrapper: {
    backgroundColor: '#d73a49',
    color: '#fff',
    padding: '8px 25px 10px 25px',
    borderRadius: 10,
  },
  errorWrapper: {
    backgroundColor: '#d73a49',
    padding: '8px 25px 10px 25px',
    borderRadius: 10,
    color: '#fff',
  },
  actionWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 10,
    [theme.breakpoints.up('sm')]: {
      marginTop: 40,
    },
  },
  tableCellRoot: {
    verticalAlign: 'middle',
  },
  quantitySelectorWrapper: {
    position: 'relative',
    top: 6,
  },
  outOfStockName: {
    color: '#838589',
  },
  outOfStockLabel: {
    color: '#838589',
    textAlign: 'center',
  },
});

const ConfigureBundleProductForm = inject('store')(
  observer(
    ({
      store: { orderStore },
      classes,
      product_detail_id,
      subItems = [],
      onSubmit = () => {},
      bundleMin = 0,
      bundleMax = 0,
      bundle_discount_type = [],
      bundle_criteria,
    }) => {
      const { t } = useTranslation('common');
      const [subProducts, setSubProducts] = useState([]);
      const [finalSubProducts, setFinalSubProducts] = useState([]);
      const [selectedSubProducts, setSelectedSubProducts] = useState([]);
      const [isLoading, setIsLoading] = useState(true);

      useEffect(() => {
        setSelectedSubProducts(subItems);
      }, [subItems]);

      useEffect(() => {
        const newSubProducts = _cloneDeep(subProducts);
        if (selectedSubProducts.length > 0) {
          newSubProducts.map((data, i) => {
            const foundIndex = _findIndex(selectedSubProducts, { product_detail_id: data.id });
            if (foundIndex > -1) {
              newSubProducts[i].quantity = selectedSubProducts[foundIndex].quantity;
            } else {
              newSubProducts[i].quantity = null;
            }
          });
          setFinalSubProducts(newSubProducts);
        } else {
          newSubProducts.map((data, i) => {
            newSubProducts[i].quantity = null;
          });
          setFinalSubProducts(newSubProducts);
        }
      }, [selectedSubProducts, subProducts]);

      useEffect(() => {
        if (product_detail_id) {
          setIsLoading(true);
          orderStore.getSubProducts({ product_detail_id }).then(res => {
            setIsLoading(false);
            const _subProducts =
              _get(res, 'data.data', []).length > 0
                ? _get(res, 'data.data', []).map(data => {
                    return { ...data, stock_quantity: data.quantity, quantity: null };
                  })
                : [];
            setSubProducts(_subProducts);
          });
        } else {
          setSubProducts([]);
        }
      }, [product_detail_id]);

      const getTotalSelectedQuantity = (_selectedSubProducts = []) => {
        let totalQuantity = 0;
        if (_selectedSubProducts.length > 0) {
          _selectedSubProducts.map(({ quantity = 0 }) => (totalQuantity += quantity));
        }
        return totalQuantity;
      };

      const totalSelectedQuantity =
        selectedSubProducts.length > 0 ? getTotalSelectedQuantity(selectedSubProducts) : 0;

      const onChangeQty = useCallback(
        (_product_detail_id, data, quantity) => {
          const newSelectedSubProducts = _cloneDeep(selectedSubProducts);
          const foundIndex = _findIndex(newSelectedSubProducts, {
            product_detail_id: _product_detail_id,
          });
          if (foundIndex > -1) {
            if (quantity <= 0) {
              newSelectedSubProducts.splice(foundIndex, 1);
            } else {
              newSelectedSubProducts[foundIndex].quantity = quantity;
            }
          } else {
            if (quantity > 0) {
              newSelectedSubProducts.push({
                ...data,
                product_detail_id: _product_detail_id,
                quantity: quantity,
              });
            }
          }
          setSelectedSubProducts(newSelectedSubProducts);
          return;
        },
        [selectedSubProducts, bundleMax]
      );

      const onClickAddToBundle = () => {
        if (bundleMax > 0) {
          if (totalSelectedQuantity <= bundleMax) {
            onSubmit(selectedSubProducts);
          }
        } else {
          onSubmit(selectedSubProducts);
        }
      };

      const getIsDisabledAddIntoBundle = () => {
        if (bundleMax) {
          if (totalSelectedQuantity > bundleMax) {
            return true;
          }
        }
        if (bundleMin) {
          if (totalSelectedQuantity < bundleMin) {
            return true;
          }
        }
        return false;
      };

      return (
        <>
          {bundle_discount_type && bundle_criteria.length > 0 ? (
            <div className={classes.bundleCriteriaWrapper}>
              <BundleCriteria
                bundle_max={bundleMax}
                bundle_discount_type={bundle_discount_type}
                bundle_criteria={bundle_criteria}
              />
            </div>
          ) : null}
          {bundleMin > totalSelectedQuantity ? (
            <p className={classes.alertWrapper}>
              {t('at-least-select', {
                value: bundleMin - totalSelectedQuantity,
              })}
            </p>
          ) : null}
          {bundleMax > 0 && totalSelectedQuantity > bundleMax ? (
            <p className={classes.errorWrapper}>
              {t('maximum-select-is', {
                bundleMax: bundleMax,
              })}
            </p>
          ) : null}
          {isLoading ? (
            <Grid container alignContent="center" justify="center">
              <Grid item>
                <br />
                <CircularProgress />
              </Grid>
            </Grid>
          ) : (
            <TableContainer className={classes.container}>
              <Table stickyHeader className={classes.table} size="small">
                <TableBody>
                  {finalSubProducts.length > 0 ? (
                    finalSubProducts.map(row => {
                      const name = _get(row, 'product.has_variation')
                        ? `${_get(row, 'product.name')} / ${_get(row, 'name')}`
                        : _get(row, 'name');
                      const isOutOfStock =
                        _get(row, 'product.ignore_oos') === false &&
                        _get(row, 'product.track_quantity') === true &&
                        _get(row, 'stock_quantity', 0) <= 0;
                      const isNotAvailable = _get(row, 'product.availability') === false;
                      return (
                        <TableRow key={row.id}>
                          <TableCell
                            component="th"
                            scope="row"
                            classes={{ root: classes.tableCellRoot }}
                            className={isOutOfStock || isNotAvailable ? classes.outOfStockName : ''}
                          >
                            {name}
                          </TableCell>
                          <TableCell align="right">
                            {isOutOfStock || isNotAvailable ? (
                              <div
                                className={
                                  isOutOfStock || isNotAvailable ? classes.outOfStockLabel : ''
                                }
                              >
                                {isNotAvailable ? t('no-longer-available') : t('out-of-stock')}
                              </div>
                            ) : (
                              <div className={classes.quantitySelectorWrapper}>
                                <QuantitySelector
                                  startingQuantity={0}
                                  value={_get(row, 'quantity', 0)}
                                  onQtyChanged={onChangeQty.bind(this, row.id, row)}
                                  isAllowToChangeQuantity={true}
                                />
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell component="th" scope="row" colSpan={5} align="center">
                        No Items
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <div className={classes.actionWrapper}>
            <p level={3}>
              {t('selected-product-quantity')}:{' '}
              {bundleMax ? `${totalSelectedQuantity}/${bundleMax}` : totalSelectedQuantity}
            </p>
            <Button
              disabled={getIsDisabledAddIntoBundle()}
              color="primary"
              variant="contained"
              onClick={() => onClickAddToBundle()}
            >
              {t('add-into-bundle')}
            </Button>
          </div>
        </>
      );
    }
  )
);

ConfigureBundleProductForm.propTypes = {
  classes: PropTypes.object.isRequired,
  product_detail_id: PropTypes.number,
  subItems: PropTypes.array,
  onSubmit: PropTypes.func.isRequired,
  bundleMin: PropTypes.number,
  bundleMax: PropTypes.number,
  bundle_discount_type: PropTypes.string,
  bundle_criteria: PropTypes.array,
};
export default withStyles(styles)(ConfigureBundleProductForm);
